import React from "react";
import PropTypes from "prop-types";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import { useGetRefundAmount } from "../../../../services/queries";
import {
  Container,
  Title,
  RefundContent,
  RefundInfo,
  RefundAmount,
  RefundNote,
} from "./UpcomingRefundComponent.styled";
import TextView from "../../../TextView";
import { Stack } from "@mui/material";
import IyugaTooltip from "../../../IyugaTooltip";

/**
 * Skeleton loader component for the refund information
 */
const RefundSkeleton = () => (
  <Container style={{ width: "100%" }}>
    <Title>
      Upcoming Refund
      <InfoOutlinedIcon style={{ color: "#9A999A", fontSize: 18 }} />
    </Title>
    <RefundContent>
      <RefundInfo style={{ width: "100%" }}>
        <Box width="100%">
          <Skeleton
            variant="text"
            width={"40%"}
            height={24}
            animation="wave"
            sx={{
              bgcolor: "rgba(155, 90, 248, 0.1)",
              borderRadius: "4px",
              marginBottom: "8px",
            }}
          />
        </Box>
        <Box width="100%">
          <Skeleton
            variant="text"
            width={"80%"}
            height={18}
            animation="wave"
            sx={{
              bgcolor: "rgba(155, 90, 248, 0.05)",
              borderRadius: "4px",
            }}
          />
        </Box>
      </RefundInfo>
    </RefundContent>
  </Container>
);

/**
 * Component to display refund information during subscription changes
 */
const UpcomingRefundComponent = () => {
  // Use the React Query hook to fetch refund data
  const { data: refundData, isLoading, error } = useGetRefundAmount({});

  // Use prop data if provided, otherwise use data from the API
  const displayData = refundData;

  if (isLoading) {
    return <RefundSkeleton />;
  }

  if (error || !displayData) {
    return (
      <Container>
        <Title>Upcoming Refund</Title>
        <RefundContent>
          <RefundInfo>
            <RefundAmount>Unable to calculate refund at this time</RefundAmount>
          </RefundInfo>
        </RefundContent>
      </Container>
    );
  }

  // Format the refund amount with currency
  const formattedAmount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: displayData.currency || "USD",
  }).format(displayData.prorated_refund_amount || 0);

  // Calculate the date range

  const totalDays = displayData.total_days;

  const TooltipComponent = () => (
    <Stack direction="column" spacing={1} sx={{}}>
      <TextView
        style={{
          fontFamily: "DM Sans",
          fontStyle: "normal",
          fontWeight: 700,
          color: "#123051",
        }}
      >
        How was this refund calculated?
      </TextView>

      <TextView
        style={{
          fontSize: "10px",
          lineHeight: "20px",
          color: "#123051",
        }}
      >
        Based on your remaining time in the billing cycle, we calculate your
        refund proportionally. (e.g., $ {displayData.invoice_amount} x{" "}
        {displayData.remaining_days} / {totalDays} = {formattedAmount})
      </TextView>
    </Stack>
  );

  return (
    <Container>
      <Title>
        Upcoming Refund
        <IyugaTooltip placement="right" tooltip={<TooltipComponent />} width="340px">
          <InfoOutlinedIcon style={{ color: "#9A999A", fontSize: 18 }} />
        </IyugaTooltip>
      </Title>

      <RefundContent>
        <RefundInfo>
          <RefundAmount>
            {formattedAmount} ({displayData.remaining_days} out of {totalDays}{" "}
            Days unused in current subscription)
          </RefundAmount>

          <RefundNote>
            *The refund will be issued to your selected payment method within
            5-10 business days
          </RefundNote>
        </RefundInfo>
      </RefundContent>
    </Container>
  );
};

UpcomingRefundComponent.propTypes = {
  refundInfo: PropTypes.shape({
    prorated_refund_amount: PropTypes.number,
    currency: PropTypes.string,
    current_period_start: PropTypes.string,
    current_period_end: PropTypes.string,
    remaining_days: PropTypes.number,
    original_amount: PropTypes.number,
  }),
};

export default UpcomingRefundComponent;
